import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCirclePlay } from '@fortawesome/pro-regular-svg-icons/faCirclePlay';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';

/**
 * Identifiers for the mobile bottom-nav sections.
 *
 * `schedule` is intentionally part of the union ahead of time so adding the
 * "My Schedule" section later is a single registry entry (see
 * {@link SECTION_CONFIG}) plus its availability flag + overlay in the layout.
 */
export type SectionId = 'related' | 'ai' | 'schedule';

/**
 * A resolved bottom-nav section (button metadata). Overlay content is rendered
 * by the layout, not carried here, so the panel/iframe instances stay owned by
 * the layout and are never recreated from this registry.
 */
export type MobileSection = {
  id: SectionId;
  label: string;
  icon: IconDefinition;
};

/**
 * Static, ordered config for each section. Order here is the bottom-nav order.
 * Adding a new section (e.g. `schedule`) means adding one entry here plus
 * wiring its `available` flag through {@link buildMobileSections} and its
 * overlay in the layout.
 */
const SECTION_CONFIG: ReadonlyArray<{ id: SectionId; icon: IconDefinition }> = [
  { id: 'related', icon: faCirclePlay },
  { id: 'ai', icon: faSparkles },
];

export type BuildMobileSectionsArgs = {
  /** Localized labels keyed by section id. */
  labels: Record<SectionId, string>;
  /** Per-section availability flags. Only available sections are returned. */
  available: Partial<Record<SectionId, boolean>>;
};

/**
 * Resolves the ordered list of *available* mobile bottom-nav sections from
 * static config plus caller-provided labels and availability flags.
 */
export function buildMobileSections({
  labels,
  available,
}: BuildMobileSectionsArgs): MobileSection[] {
  return SECTION_CONFIG.filter(({ id }) => available[id]).map(
    ({ id, icon }) => ({
      id,
      icon,
      label: labels[id],
    }),
  );
}
